import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditMultiSelectField } from '../../../EditMultiSelectField';
import type {
  ContactMerrillLynchData,
  MlCoverageCitiesData,
  MlRegionalCoverageOptionsData
} from '../queries/contactMerrillLynchQuery';
import {
  GET_CONTACT_MERRILL_LYNCH,
  GET_ML_COVERAGE_CITIES,
  GET_ML_REGIONAL_COVERAGE_OPTIONS,
  mapContactMerrillLynch
} from '../queries/contactMerrillLynchQuery';

function getChangedValues(
  current: ContactMerrillLynchData,
  initial: ContactMerrillLynchData
): Record<string, null | string | string[]> {
  const changes: Record<string, null | string | string[]> = {};

  if (current.mlComplex !== initial.mlComplex)
    changes.mlComplex = current.mlComplex || null;
  if (current.mlOffice !== initial.mlOffice)
    changes.mlOffice = current.mlOffice || null;
  if (current.mlRegion !== initial.mlRegion)
    changes.mlRegion = current.mlRegion || null;

  // Compare arrays of IDs
  const currentRegionalIds = [...current.mlRegionalCoverageIds].sort();
  const initialRegionalIds = [...initial.mlRegionalCoverageIds].sort();
  if (JSON.stringify(currentRegionalIds) !== JSON.stringify(initialRegionalIds))
    changes.mlRegionalCoverageIds =
      current.mlRegionalCoverageIds.length > 0
        ? current.mlRegionalCoverageIds
        : null;

  const currentHfIds = [...current.mlHfCityIds].sort();
  const initialHfIds = [...initial.mlHfCityIds].sort();
  if (JSON.stringify(currentHfIds) !== JSON.stringify(initialHfIds))
    changes.mlHfCityIds =
      current.mlHfCityIds.length > 0 ? current.mlHfCityIds : null;

  const currentPeIds = [...current.mlPeCityIds].sort();
  const initialPeIds = [...initial.mlPeCityIds].sort();
  if (JSON.stringify(currentPeIds) !== JSON.stringify(initialPeIds))
    changes.mlPeCityIds =
      current.mlPeCityIds.length > 0 ? current.mlPeCityIds : null;

  return changes;
}

const UPSERT_CONTACT_ML_DATA = gql`
  mutation UpsertContactMlData(
    $contactId: ID!
    $values: ContactMerrillLynchDataInput!
  ) {
    upsertContactMlData(contactId: $contactId, values: $values) {
      ok
      created
      contactMlData {
        id
        mlOffice
        mlComplex
        mlRegion
        mlRegionalCoverageIds
        mlHfCityIds
        mlPeCityIds
        contactId {
          id
          name
        }
      }
    }
  }
`;

export interface EditContactMerrillLynchDialogProps {
  contactId: string;
  initialValue: ContactMerrillLynchData;
  isOpen: boolean;
  onChange: (data: ContactMerrillLynchData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactMerrillLynchData;
}

export function EditContactMerrillLynchDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditContactMerrillLynchDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  // Fetch options for Many2many fields
  const { data: citiesData, loading: loadingCities } =
    useQuery<MlCoverageCitiesData>(GET_ML_COVERAGE_CITIES, {
      fetchPolicy: 'cache-first'
    });
  const { data: regionalData, loading: loadingRegional } =
    useQuery<MlRegionalCoverageOptionsData>(GET_ML_REGIONAL_COVERAGE_OPTIONS, {
      fetchPolicy: 'cache-first'
    });

  const [upsertContactMlData] = useMutation(UPSERT_CONTACT_ML_DATA, {
    refetchQueries: [
      { query: GET_CONTACT_MERRILL_LYNCH, variables: { contactId } }
    ]
  });

  // Convert options to multi-select format
  const cityOptions = useMemo(() => {
    if (!citiesData?.mlCoverageCities) return [];
    return citiesData.mlCoverageCities.map(city => ({
      label: city.name,
      value: city.id
    }));
  }, [citiesData]);

  const regionalOptions = useMemo(() => {
    if (!regionalData?.mlRegionalCoverageOptions) return [];
    return regionalData.mlRegionalCoverageOptions.map(option => ({
      label: option.name,
      value: option.id
    }));
  }, [regionalData]);

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactMerrillLynchData>(
    field: K,
    fieldValue: ContactMerrillLynchData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertContactMlData({
        variables: { contactId, values: changes }
      });

      const data = response.data as {
        upsertContactMlData?: { contactMlData?: unknown; ok?: boolean };
      };

      if (data.upsertContactMlData?.ok === false) {
        toast.error('Failed to save Merrill Lynch data');
      } else {
        toast.success('Merrill Lynch data saved successfully');
        if (data.upsertContactMlData?.contactMlData) {
          onChange(
            mapContactMerrillLynch(
              data.upsertContactMlData.contactMlData as Parameters<
                typeof mapContactMerrillLynch
              >[0]
            )
          );
        }
        onSave();
      }
    } catch (error) {
      console.error('Error saving Merrill Lynch data:', error);
      toast.error('Failed to save Merrill Lynch data');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Merrill Lynch Data"
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <EditDynamicSelectField
          fieldName="ml_complex"
          label="ML Complex"
          model="contact.ml.data"
          onChange={v => {
            updateField('mlComplex', v);
          }}
          value={value.mlComplex}
        />
        <EditDynamicSelectField
          fieldName="ml_office"
          label="ML Office"
          model="contact.ml.data"
          onChange={v => {
            updateField('mlOffice', v);
          }}
          value={value.mlOffice}
        />
        <EditDynamicSelectField
          fieldName="ml_region"
          label="ML Region"
          model="contact.ml.data"
          onChange={v => {
            updateField('mlRegion', v);
          }}
          value={value.mlRegion}
        />
        <EditMultiSelectField
          label="ML Regional Coverage"
          onChange={ids => {
            updateField('mlRegionalCoverageIds', ids);
          }}
          options={regionalOptions}
          placeholder={
            loadingRegional ? 'Loading...' : 'Select regional coverage...'
          }
          values={value.mlRegionalCoverageIds}
        />
        <EditMultiSelectField
          label="ML HF Specialist Coverage Cities"
          onChange={ids => {
            updateField('mlHfCityIds', ids);
          }}
          options={cityOptions}
          placeholder={loadingCities ? 'Loading...' : 'Select cities...'}
          values={value.mlHfCityIds}
        />
        <EditMultiSelectField
          label="ML PE Specialist Coverage Cities"
          onChange={ids => {
            updateField('mlPeCityIds', ids);
          }}
          options={cityOptions}
          placeholder={loadingCities ? 'Loading...' : 'Select cities...'}
          values={value.mlPeCityIds}
        />
      </div>
    </EditDialog>
  );
}
